import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import ProfileMenuIcon from 'components/atoms/icons/profileMenuIconNew';
import CollapseAnimation from 'components/atoms/base-animations/collapse';
import ProfileIcon from 'components/atoms/icons/profileIconNew';
import LogoutIcon from 'components/atoms/icons/logoutIconNew';
import useUser from 'utils/useUser';
import { Routes } from 'router/routes';
import useLogout from 'utils/hooks/auth/useLogout';

import s from './styles.module.scss';
import MobileHeaderMenus from './menu';

import type { IDropdownLinks } from '../types';

interface IBodyProps {
  opened: boolean;
  showNavigation: boolean;
  links: IDropdownLinks[];
}

const Body: React.FC<IBodyProps> = ({ opened, links, showNavigation }) => {
  const { t } = useTranslation();
  const { logout } = useLogout();

  const user = useUser();

  const { isCriticalOrAtRiskAlertingRole, isPatient, isAdmin, isAgencyDoctor } =
    user;

  return (
    <div className={s.wrapper}>
      <CollapseAnimation opened={opened}>
        <ul className={s.body}>
          <div>
            <Link
              className={classNames(s.item, s.name)}
              to={!isCriticalOrAtRiskAlertingRole ? Routes.profile : '/'}
              data-cy="nav-profile__edit"
            >
              <ProfileIcon />
              {user.fullName}
            </Link>

            {showNavigation &&
              links.map((link) => (
                <MobileHeaderMenus key={link.label} data={link} />
              ))}

            {!isPatient && (
              <>
                <Link
                  data-cy="nav-profile__telemed"
                  className={s.item}
                  to={Routes.telmed!}
                  target="_blank"
                >
                  {t('links.telemed')}
                </Link>
                <Link
                  data-cy="nav-profile__bucket-report"
                  className={s.item}
                  to={Routes.bucketReport!}
                  target="_blank"
                >
                  {t('links.filesCom')}
                </Link>
              </>
            )}
            {(isAdmin || isAgencyDoctor) && (
              <Link
                data-cy="nav-profile__aws-connect"
                className={s.item}
                to={Routes.awsConnectURL!}
                target="_blank"
              >
                {t('links.amazonConnect')}
              </Link>
            )}
          </div>
          <li
            className={classNames(s.item, s.logout)}
            onClick={() => {
              logout();
            }}
            data-cy="nav-profile__logout"
          >
            {t('links.logout')}
            <LogoutIcon />
          </li>
        </ul>
      </CollapseAnimation>
    </div>
  );
};

interface IProps {
  links: IDropdownLinks[];
  showNavigation: boolean;
}

const MobileHeader: React.FC<IProps> = (props) => {
  const { links, showNavigation } = props;
  const [opened, setOpened] = useState(false);

  const location = useLocation();

  const toggleClose = () => {
    setOpened(!opened);
  };

  useEffect(() => {
    if (opened) {
      window.document.body.style.overflowY = 'hidden';
    } else {
      window.document.body.style.overflowY = 'auto';
    }

    return () => {
      window.document.body.style.overflowY = 'auto';
    };
  }, [opened]);

  useEffect(() => {
    if (opened) setOpened(false);
  }, [location.pathname]);

  return (
    <nav>
      <span onClick={toggleClose} data-cy="nav-profile__burger-menu">
        <ProfileMenuIcon animate={opened} />
      </span>
      <Body opened={opened} showNavigation={showNavigation} links={links} />
    </nav>
  );
};

export default MobileHeader;
